<template>
    <access-denied v-if="accessDenied" title="empresa" />
    <div v-else>
        <alert-container :messages="msgs" />

        <div class="listing-header mb-4">
            <div class="title-breadcrumb"></div>
            <div class="header-buttons">
                <button
                    v-if="(!isUpdate && permissao.create) || (isUpdate && permissao.update)"
                    @click="save"
                    :disabled="isSaving"
                    class="save-btn"
                    type="button"
                >
                    {{ $t('salvar') }}
                    <button-loader :isSaving="isSaving" />
                </button>
            </div>
        </div>

        <div class="page-container form-columns-fit">
            <div class="columns is-flex-wrap">
                <div class="column is-3 img-picker">
                    <ImgPicker
                        ref="imgPicker"
                        :logourl="empresa.logo_url_print"
                        @uploadedFiles="getLogo"
                        @alert="showMessage"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column is-9">
                    <div class="columns is-flex-wrap">
                        <div class="column is-3">
                            <v-input
                                :id="generateId(1)"
                                ref="codigoInput"
                                v-model="empresa.idempresaerp"
                                :maxlength="20"
                                title="codigo"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(empresa.idempresaerp) && !empresaIntegracao"
                                required
                                :disabled="isUpdate || empresaIntegracao"
                                tooltipLabel="informacao-empresa-codigo"
                                tooltipSize="is-small"
                            />
                        </div>
                        <div class="column is-9">
                            <v-input
                                :id="generateId(2)"
                                ref="nomeInput"
                                v-model="empresa.nome"
                                :maxlength="50"
                                title="nome"
                                msg="msg-campo-obrigatorio"
                                :invalid="isNull(empresa.nome) && !empresaIntegracao"
                                required
                                tooltipLabel="informacao-empresa-nome"
                                tooltipSize="is-small"
                                :disabled="cantEdit || empresaIntegracao"
                            />
                        </div>
                        <div class="column is-7">
                            <v-input
                                :id="generateId(3)"
                                ref="razaosocialInput"
                                v-model="empresa.rsocial"
                                :maxlength="255"
                                title="razaosocial"
                                :required="required.rsocial.includes($route.meta.abrev)"
                                tooltipLabel="informacao-empresa-rsocial"
                                tooltipSize="is-small"
                                :disabled="cantEdit || empresaIntegracao"
                                :invalid="
                                    required.rsocial.includes($route.meta.abrev) &&
                                    isNull(empresa.rsocial) &&
                                    !empresaIntegracao
                                "
                            />
                        </div>
                        <div class="column is-5">
                            <vss-select
                                :id="generateId(4)"
                                ref="moedaSelect"
                                title="simbolo-monetario"
                                :searchable="false"
                                :options="moedas"
                                v-model="moeda"
                                class="no-clear"
                                tooltipLabel="informacao-empresa-moeda"
                                tooltipPosition="is-left"
                                :disabled="cantEdit || empresaIntegracao"
                                :required="required.moeda.includes($route.meta.abrev)"
                                @onBlurFunction="sendIsChanged()"
                            />
                        </div>
                        <div class="column is-3" :class="{ loading: isSearchingCNPJ }">
                            <v-input
                                :id="generateId(5)"
                                ref="cnpjInput"
                                :hideInfo="hide.hideInfo"
                                :infoIsHidden="hide.infoIsHidden"
                                v-model="empresa.cnpj"
                                title="cnpj-do-cliente"
                                placeholder="cnpj-do-cliente-placeholder"
                                :msg="invalidCNPJMsg"
                                :invalid="invalidCNPJ && !empresaIntegracao"
                                :onBlurFunction="validateCNPJInner"
                                v-mask="['##.###.###/####-##']"
                                :icon="cantEdit || empresaIntegracao ? null : 'magnify'"
                                @input-action="getInfoByCNPJ"
                                :required="required.cnpj.includes($route.meta.abrev)"
                                tooltipLabel="informacao-empresa-cnpj"
                                :disabled="cantEdit || empresaIntegracao"
                            />
                        </div>
                        <div class="column is-5">
                            <vss-select
                                :id="generateId(89)"
                                ref="contribuinte"
                                title="contribuinte"
                                :searchable="false"
                                :options="contribuinteSelect"
                                v-model="contribuinteSelected"
                                :mlLabels="true"
                                tooltipLabel="informacao-cliente-contribuinte"
                                tooltipSize="is-small"
                                tooltipPosition="is-top"
                                :disabled="cantEdit"
                            />
                        </div>
                        <div class="column is-4">
                            <v-input
                                :id="generateId(6)"
                                ref="ieInput"
                                v-model="empresa.ie"
                                :maxlength="50"
                                title="ie"
                                :msg="invalidIEMsg"
                                :invalid="invalidIE && !empresaIntegracao"
                                :onBlurFunction="validateIEInner"
                                :required="required.cnpj.includes($route.meta.abrev)"
                                tooltipLabel="informacao-empresa-ie"
                                tooltipSize="is-small"
                                :disabled="cantEdit || empresaIntegracao || contribuinteSelected.id != 'C'"
                            />
                        </div>
                    </div>
                </div>

                <div class="column is-3">
                    <v-input
                        :id="generateId(7)"
                        ref="porcentagemestipuladaInput"
                        v-model="empresa.porcentagemestipulada"
                        :maxlength="3"
                        title="porcentagemestipulada"
                        :msg="invalidPorcentagemMsg"
                        :invalid="invalidPorcentagem && !empresaIntegracao"
                        :onBlurFunction="validatePorcentagemInner"
                        :required="required.porcentagemestipulada.includes($route.meta.abrev)"
                        tooltipLabel="informacao-empresa-porcentagemestipulada"
                        tooltipPosition="is-top"
                        v-mask="['###']"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-4" :class="{ loading: isSearchingCEP }">
                    <v-input
                        :id="generateId(8)"
                        ref="cepInput"
                        :hideInfo="hide.hideInfo"
                        :infoIsHidden="hide.infoIsHidden"
                        v-model="empresa.cep"
                        title="cep-do-cliente"
                        placeholder="cep-do-cliente-placeholder"
                        :msg="invalidCEPMsg"
                        :invalid="invalidCEP && !empresaIntegracao"
                        :onBlurFunction="validateCEPInner"
                        v-mask="['##.###-###']"
                        :icon="cantEdit || empresaIntegracao ? null : 'magnify'"
                        @input-action="getInfoByCEP"
                        :required="required.cep.includes($route.meta.abrev)"
                        tooltipLabel="informacao-empresa-cep"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-5">
                    <vss-select
                        :id="generateId(9)"
                        ref="cidadeInput"
                        label="nome"
                        v-model="cidade"
                        placeholder="selecione"
                        title="cidade"
                        :options="cidades"
                        msg="msg-campo-obrigatorio"
                        :invalid="isNull(cidade) && !empresaIntegracao"
                        searchable
                        @search="loadCidade"
                        :required="required.cidade.includes($route.meta.abrev)"
                        :onChange="updateCidade()"
                        :onBlurFunction="validateIEInner"
                        tooltipLabel="informacao-empresa-cidade"
                        tooltipSize="is-small"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-4">
                    <v-input
                        :id="generateId(10)"
                        ref="bairroInput"
                        v-model="empresa.bairro"
                        :maxlength="50"
                        title="bairro"
                        msg="msg-campo-obrigatorio"
                        :invalid="isNull(empresa.bairro) && !empresaIntegracao"
                        :required="required.bairro.includes($route.meta.abrev)"
                        tooltipLabel="informacao-empresa-bairro"
                        tooltipSize="is-small"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-6">
                    <v-input
                        :id="generateId(11)"
                        ref="enderecoInput"
                        v-model="empresa.endereco"
                        :maxlength="50"
                        title="endereco"
                        msg="msg-campo-obrigatorio"
                        :invalid="isNull(empresa.endereco) && !empresaIntegracao"
                        :required="required.endereco.includes($route.meta.abrev)"
                        tooltipLabel="informacao-empresa-endereco"
                        tooltipSize="is-small"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-2">
                    <v-input
                        :id="generateId(12)"
                        ref="numeroInput"
                        v-model="empresa.numero"
                        :maxlength="50"
                        title="numero"
                        msg="msg-campo-obrigatorio"
                        :invalid="isNull(empresa.numero)"
                        :required="required.numero.includes($route.meta.abrev)"
                        :useMask="true"
                        :vMask="['##########']"
                        tooltipLabel="informacao-empresa-numero"
                        tooltipSize="is-small"
                        tooltipPosition="is-top"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column is-6">
                    <v-input
                        :id="generateId(13)"
                        ref="latitudeInput"
                        v-model="empresa.latitude"
                        :maxlength="50"
                        title="latitude"
                        :msg="invalidLatitudeMsg"
                        :invalid="invalidLatitude"
                        :onBlurFunction="validateLatitudeInner"
                        :required="required.latitude.includes($route.meta.abrev)"
                        tooltipLabel="informacao-empresa-latitude"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column" :class="[windowWidth > 920 ? 'is-4' : 'is-6']">
                    <div class="field has-addons">
                        <div class="control is-expanded">
                            <v-input
                                :id="generateId(14)"
                                ref="longitudeInput"
                                v-model="empresa.longitude"
                                :maxlength="50"
                                title="longitude"
                                :msg="invalidLongitudeMsg"
                                :invalid="invalidLongitude"
                                :onBlurFunction="validateLongitudeInner"
                                :required="required.longitude.includes($route.meta.abrev)"
                                tooltipLabel="informacao-empresa-longitude"
                                :disabled="cantEdit"
                            />
                        </div>
                        <div
                            v-if="!cantEdit && windowWidth <= 920"
                            class="control padding-label padding-label-custom"
                        >
                            <b-button
                                @click="openLocationEditor"
                                icon-right="map-marker"
                                type="is-primary"
                                v-tippy 
                                :title="$t('pesquisar-localizacao')"
                            />
                        </div>
                    </div>
                </div>
                <div class="column is-2" v-if="windowWidth > 920 && !cantEdit">
                    <button
                        class="save-btn botao-localizacao"
                        type="button"
                        @click="openLocationEditor"
                    >
                        {{ $t('pesquisar-localizacao') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="page-container form-columns-fit mt-4 mb-4">
            <div class="columns is-flex-wrap">
                <div class="column is-4">
                    <v-input-tel
                        :id="generateId(20)"
                        :hideInfo="hide.hideInfo"
                        :infoIsHidden="hide.infoIsHidden"
                        ref="telefoneInput"
                        v-model="empresa.telefone"
                        :maxlength="20"
                        title="telefone"
                        :msg="invalidTelefoneMsg"
                        :invalid="invalidTelefone && !empresaIntegracao"
                        :onBlurFunction="validateTelefone"
                        :required="required.telefone.includes($route.meta.abrev)"
                        :edit="inputTelefoneEdit"
                        tooltipLabel="informacao-empresa-telefone"
                        tooltipSize="is-small"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                </div>
                <div class="column is-4">
                    <v-input-tel
                        :id="generateId(21)"
                        :hideInfo="hide.hideInfo"
                        :infoIsHidden="hide.infoIsHidden"
                        ref="celularInput"
                        v-model="empresa.telefone_celular"
                        :maxlength="20"
                        title="celular"
                        :msg="invalidCelularMsg"
                        :invalid="invalidCelular"
                        :onBlurFunction="validateCelular"
                        :required="required.telefone_celular.includes($route.meta.abrev)"
                        :edit="inputCelularEdit"
                        :icon="cantEdit || empresaIntegracao ? null : 'content-copy'"
                        :iconTooltip="'copiar-tel-para-o-campo-wpp'"
                        @input-action="copyWhatsApp"
                        tooltipLabel="informacao-empresa-celular"
                        tooltipSize="is-small"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column is-4">
                    <v-input-tel
                        :id="generateId(22)"
                        ref="faxInput"
                        :hideInfo="hide.hideInfo"
                        :infoIsHidden="hide.infoIsHidden"
                        v-model="empresa.fax"
                        :maxlength="20"
                        title="fax-do-cliente"
                        :edit="inputFaxEdit"
                        :msg="invalidFaxMsg"
                        :invalid="invalidFax && !empresaIntegracao"
                        :onBlurFunction="validateFax"
                        v-if="!copyWhatsAppActive"
                        tooltipLabel="informacao-empresa-fax"
                        tooltipSize="is-small"
                        :disabled="cantEdit || empresaIntegracao"
                    />
                    <v-input v-else disabled title="fax-do-cliente" />
                </div>

                <div class="column is-4">
                    <v-input
                        :id="generateId(23)"
                        ref="paginawebInput"
                        v-model="empresa.paginaweb"
                        :maxlength="50"
                        title="paginaweb"
                        :msg="invalidURLMsg"
                        :invalid="invalidURL"
                        :onBlurFunction="validateURLInner"
                        :uppercase="false"
                        icon="open-in-new"
                        @input-action="openLink(empresa.paginaweb)"
                        tooltipLabel="informacao-empresa-paginaweb"
                        tooltipSize="is-small"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column is-4">
                    <v-input
                        :id="generateId(24)"
                        ref="email1Input"
                        v-model="empresa.email1"
                        :maxlength="50"
                        title="email1"
                        :msg="invalidEmail1Msg"
                        :invalid="invalidEmail1"
                        :onBlurFunction="validateEmail1"
                        :required="required.email1.includes($route.meta.abrev)"
                        iconTooltip="copiar-email-para-o-campo-email-2"
                        :icon="cantEdit || empresaIntegracao ? null : 'content-copy'"
                        @input-action="copyEmail"
                        type="email"
                        tooltipLabel="informacao-empresa-email1"
                        tooltipSize="is-small"
                        :disabled="cantEdit"
                    />
                </div>
                <div class="column is-4">
                    <v-input
                        :id="generateId(25)"
                        ref="email2Input"
                        v-model="empresa.email2"
                        :maxlength="50"
                        title="email2"
                        :msg="invalidEmail2Msg"
                        :invalid="invalidEmail2"
                        :onBlurFunction="validateEmail2"
                        type="email"
                        tooltipLabel="informacao-empresa-email2"
                        tooltipSize="is-small"
                        :disabled="cantEdit"
                    />
                </div>
            </div>
        </div>
        <MapasGoogleMapsModal
            v-if="permissao.update && startedMaps"
            :center="center"
            :isUpdating="isSaving"
            :locationEditorOpen="locationEditorOpen"
            :update="isUpdate"
            label="selecionar"
            @updateLocation="updateLocation"
            @onCancelLocationEditor="onCancelLocationEditor"
            :startSearchWith="startSearchWith"
        />
    </div>
</template>

<script>
import AssClienteEmpresaService from '@/services/vendas/cliente/abas/AssClienteEmpresaService';
import EmpresaService from '@/services/vendas/empresa/EmpresaService';
import CidadeService from '@/services/CidadeService';
import MoedaService from '@/services/vendas/MoedaService';
import EmpresaValidation from '@/services/validations/vendas/empresa/EmpresaGeralValidation';
import HideInfo from '@/components/shared/HideInfo';
import Message from '@/models/Message';
import { TheMask } from 'vue-the-mask';
import * as InscricaoEstadual from '@/functions/IEValidation';
import { mapState } from 'vuex';
import MapasGoogleMapsModal from '@/components/shared/maps/MapasGoogleMapsModal';
import CidadeModal from '@/components/vendas/empresa/modal/CidadeModal';
import ImgPicker from '@/components/shared/form/ImgPicker';

export default {
    components: {
        TheMask,
        HideInfo,
        MapasGoogleMapsModal,
        CidadeModal,
        ImgPicker,
    },
    props: {
        isUpdate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            windowWidth: window.innerWidth,
            assClienteEmpresaIntegracao: this.$store.state.user.tabelasERPWebServer.includes(
                'ASSCLIENTEEMPRESA'
            ),
            empresaIntegracao: this.$store.state.user.tabelasERPWebServer.includes('EMPRESA'),
            service: new EmpresaService(this.$http, this.$router),
            serviceAss: new AssClienteEmpresaService(this.$http, this.$router),
            cidadeservice: new CidadeService(this.$http, this.$router),
            moedaservice: new MoedaService(this.$http, this.$router),
            validation: new EmpresaValidation(this.msgs),
            cleanHide: true,
            msgs: [],

            isLoading: false,
            isSaving: false,
            permissao: false,
            permissaoVincular: false,
            isSearchingCNPJ: false,
            isSearchingCEP: false,

            required: {
                rsocial: ['ven', 'des'],
                moeda: ['des'],
                cnpj: ['ven', 'des', 'pro', 'agro'],
                ie: ['ven', 'des', 'pro', 'agro'],
                porcentagemestipulada: ['ven', 'des', 'pro', 'agro'],
                cep: ['ven', 'des', 'pro', 'agro'],
                cidade: ['ven', 'des', 'pro', 'agro'],
                bairro: ['ven', 'des', 'pro', 'agro'],
                endereco: ['ven', 'des', 'pro', 'agro'],
                numero: ['ven', 'des', 'pro', 'agro'],
                latitude: ['ven', 'pro', 'agro'],
                longitude: ['ven', 'pro', 'agro'],
                telefone: ['ven', 'des', 'pro', 'agro'],
                telefone_celular: ['ven', 'pro', 'agro'],
                email1: ['ven', 'des', 'pro', 'agro'],
            },
            invalidCNPJ: true,
            invalidCNPJMsg: 'msg-campo-obrigatorio',
            invalidCEP: true,
            invalidCEPMsg: 'msg-campo-obrigatorio',
            invalidTelefone: false,
            invalidTelefoneMsg: 'msg-campo-obrigatorio',
            invalidCelular: false,
            invalidCelularMsg: 'msg-campo-obrigatorio',
            invalidFax: false,
            invalidFaxMsg: 'msg-campo-obrigatorio',
            invalidIE: false,
            invalidIEMsg: 'msg-campo-obrigatorio',
            invalidPorcentagem: false,
            invalidPorcentagemMsg: 'msg-campo-obrigatorio',
            invalidLatitude: false,
            invalidLatitudeMsg: 'msg-campo-obrigatorio',
            invalidLongitude: false,
            invalidLongitudeMsg: 'msg-campo-obrigatorio',
            invalidEmail1: false,
            invalidEmail1Msg: 'msg-campo-obrigatorio',
            invalidEmail2: false,
            invalidEmail2Msg: 'msg-campo-obrigatorio',
            invalidURL: false,
            invalidURLMsg: 'msg-campo-obrigatorio',

            inputFaxEdit: false,
            inputCelularEdit: false,
            inputTelefoneEdit: false,

            empresa: {
                idempresa: null,
                idempresaerp: null,
                nome: null,
                rsocial: null,
                endereco: null,
                numero: null,
                bairro: null,
                uf: null,
                cidade: null,
                idcidade: null,
                cep: null,
                ie: null,
                cnpj: null,
                telefone: null,
                telefone_celular: null,
                fax: null,
                paginaweb: null,
                email1: null,
                email2: null,
                simbolomonetario: null,
                template: null,
                tituloorcamento: null,
                logo_url_print: null,
                aws_s3_folder: null,
                tipocalcjuros: null,
                porcentagemestipulada: 0,
                idmoeda: null,
                menuitem: null,
                latitude: null,
                longitude: null,
            },
            cidade: null,
            cidades: [],
            moeda: null,
            moedas: [],

            isCidadeModalActive: false,
            copyWhatsAppActive: false,
            center: { lat: 0, lng: 0 },
            locationEditorOpen: false,
            buscaCep: null,
            startSearchWith: '',
            cantEdit: false,
            startedMaps: true,
            // para controlar algumas ações até os dados serem carregados na edição
            isLoadingEdit: false,
            logo: [],
            accessDenied: false,
            contribuinteSelect: [
                { label: "contribuinte-icms", id: "C" },
                { label: "contribuinte-isento-ou-nao-contribuinte", id: "N" },
            ],
            contribuinteSelected: { label: "contribuinte-icms", id: "C" },
        };
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    mounted() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.onResize);
        });
    },
    computed: {
        ...mapState(['hide']),
    },
    watch: {
        empresa: {
            deep: true,
            handler: function () {
                if (!this.isLoadingEdit && !this.isLoading) {
                    this.$emit('isChanged', true);
                }
            },
        },
        contribuinteSelected: function (newValue, _) {
            this.invalidIE = false;

            if(newValue.id != 'C') {
                this.empresa.ie = 'ISENTO'
            } else {
                this.empresa.ie = null
            }
        }
    },
    created() {
        if (this.$route.meta.abrev == 'ven') {
            this.permissao = this.hasPermissionAccess(
                'vendas',
                'empresa',
                'ven-empresa-dados-cadastrais'
            );
        } else if (this.$route.meta.abrev == 'pro') {
            this.permissao = this.hasPermissionAccess(
                'promotor',
                'empresa',
                'pro-empresa-dados-cadastrais'
            );
        } else if (this.$route.meta.abrev == 'des') {
            this.permissao = this.hasPermissionAccess(
                'des',
                'empresa',
                'des-empresa-dados-cadastrais'
            );
        } else if (this.$route.meta.abrev == 'agro') {
            this.permissao = this.hasPermissionAccess(
                'agro',
                'empresa',
                'agro-empresa-dados-cadastrais'
            );
        }

        this.permissaoVincular = this.permissao.create && !this.assClienteEmpresaIntegracao;

        if ((!this.isUpdate && !this.permissao.create) || (this.isUpdate && !this.permissao.read)) {
            this.accessDenied = true;
            return
        } else {
            if (this.isUpdate) {
                this.cantEdit = !this.permissao.update;
                this.carregarDados();
            } else {
                this.loadMoedas();
                this.startedMaps = true;
            }
        }

        this.onResize();
        this.$emit('isChanged', false);
    },
    methods: {
        sendIsChanged() {
            if (!this.isLoadingEdit && !this.isLoading) this.$emit('isChanged', true);
        },
        onResize() {
            this.windowWidth = window.innerWidth;
        },
        generateId(numberId) {
            return this.$route.name + '-geral-' + numberId;
        },
        save() {
            this.isLoading = true;
            this.empresa.idmoeda = this.moeda ? this.moeda.id : null;
            if (
                this.validation.validate(
                    this.empresa,
                    this.$refs,
                    this.$route.meta.abrev,
                    this.required
                )
            ) {
                let empresaTemp = Object.assign({}, this.empresa);
                let file = this.logo && this.logo[0] ? this.logo[0].file : null;

                empresaTemp = this.mapTosave(empresaTemp, 'logo_url_print', 'paginaweb', 'cnpj');
                empresaTemp.paginaweb = this.mapToSaveURL(empresaTemp.paginaweb);

                this.isSaving = true;
                this.service
                    .save(file, empresaTemp)
                    .then((value) => {
                        if (value.message == null) {
                            let msg =
                                this.empresa.idempresa != null
                                    ? 'msg-edit-empresa'
                                    : 'msg-save-empresa';
                            this.msgs.push(new Message('success', msg, true));

                            this.$emit('isChanged', false);

                            if (!this.isUpdate) this.$emit('isNewToEdit', true);

                            if (
                                this.permissaoVincular &&
                                !this.isUpdate &&
                                this.$route.query.tela == 'cliente-empresa'
                            ) {
                                this.promptConfirmaVinculo(value.content);
                            } else {
                                if (this.$route.query.tela == 'cliente-empresa') {
                                    this.redirectToPage();
                                } else {
                                    this.redirectToEdit(value.content.id);
                                }
                            }
                        } else {
                            if (this.$t(value.message)) {
                                this.msgs.push(new Message('alert', value.message, true));
                            } else {
                                this.msgs.push(
                                    new Message('error', 'msg-problema-realizar-acao', true)
                                );
                            }
                        }
                    })
                    .catch((e) => {
                        if (e.body.status == 500) {
                            this.msgs.push(
                                /* 
                                    'msg-existe-codigo-empresa'
                                    'msg-existe-cpf-cliente'
                                    'msg-existe-cnpj-cliente'
                                */
                                new Message('alert', e.body.message, true)
                            );
                        } else {
                            console.error('Erro ao salvar: ' + e);
                            this.msgs.push(new Message('error', 'msg-erro-salvar', true));
                        }
                    })
                    .finally(() => {
                        this.isSaving = false;
                        this.isLoading = false;
                    });
            } else {
                this.runValidations();
                this.msgs.push(new Message('alert', 'msg-campos-obrigatorios', true));
                this.isLoading = false;
            }
        },
        // @85242
        realizarVinculo(vinculo) {
            this.isLoading = true;
            this.isLoadingEdit = true;

            let clienteEmpresaTemp = {
                empresa: this.empresa,
                idempresa: vinculo.idempresa,
                idcliente: this.$route.query['id'],
            };

            this.serviceAss
                .saveAss(clienteEmpresaTemp)
                .then((value) => {
                    if (value.message == null) {
                        this.$root.showMessage('success', 'msg-sucesso-ao-adicionar-associacao');
                        this.redirectToPage();
                    } else {
                        if (this.$t(value.message)) {
                            this.msgs.push(new Message('alert', value.message, true));
                        } else {
                            this.msgs.push(
                                new Message('error', 'msg-problema-realizar-acao', true)
                            );
                        }
                    }
                })
                .catch((e) => {
                    this.msgs.push(new Message('alert', 'msg-erro-load', true));
                })
                .finally(() => {
                    this.isLoading = false;
                    this.isLoadingEdit = false;
                });
        },
        // @85242
        promptConfirmaVinculo(vinculo) {
            this.$root.confirm({
                message: this.$t('deseja-realizar-vinculo'),
                onConfirm: (_) => {
                    this.realizarVinculo(vinculo);
                },
                onCancel: (_) => {
                    this.redirectToPage();
                },
            });
        },
        redirectToEdit(id) {
            let tempTela = null;
            let telaModulo = 'des';

            if (this.$route.meta.abrev == 'ven') telaModulo = 'ven';
            else if (this.$route.meta.abrev == 'pro') telaModulo = 'pro';
            else if (this.$route.meta.abrev == 'agro') telaModulo = 'agro';

            tempTela = `${telaModulo}-empresa-dados-cadastrais-edit`;

            setTimeout(() => {
                this.$router.push({
                    name: tempTela,
                    params: {
                        id: id,
                        name: this.empresa.rsocial,
                    },
                });
            }, 1000);
        },
        carregarDados() {
            this.isLoading = true;
            this.isLoadingEdit = true;
            this.service
                .find(this.$route.params.id)
                .then((value) => {
                    this.empresa = Object.assign({}, value.content);
                    if (
                        value.content.idcidade != null &&
                        value.content.idcidade != 0 &&
                        value.content.idcidade != ''
                    ) {
                        this.loadCidadeById(value.content.idcidade);
                    } else {
                        this.loadCidade(value.content.cidade, value.content.uf);
                    }
                    this.checkEditTel();
                })
                .catch((e) => {
                    console.error(e);
                    this.msgs.push(new Message('error', 'msg-erro-load', true));
                })
                .finally((_) => {
                    this.runValidations(true);
                    this.loadMoedas();
                    this.startedMaps = true;
                    
                    if(this.empresa.ie === "ISENTO") this.contribuinteSelected = { label: "contribuinte-isento-ou-nao-contribuinte", id: "N" }
                    
                    setTimeout(() => {
                        this.isLoading = false;
                        this.isLoadingEdit = false;
                    }, 2500);
                });
        },
        checkEditTel() {
            this.inputFaxEdit = !this.isNull(this.empresa.fax);
            this.inputCelularEdit = !this.isNull(this.empresa.telefone_celular);
            this.inputTelefoneEdit = !this.isNull(this.empresa.telefone);
        },
        runValidations(focusedOut = false) {
            if (focusedOut) {
                // Utilizado para ativar a validação dos campos ao carregar os dados de alteração
                Object.entries(this.$refs).forEach(([key, value]) => {
                    if (value != undefined) value.focusedOut = true;
                });
            }
            // Ao realizar o focusedOut no arquivo validation, as funções de validate não são chamadas, tendo assim que fazelas manualmente para poder apresentar os erros nos campos
            this.validateCNPJInner();
            this.validateCEPInner();
            this.validateTelefone();
            this.validateCelular();
            this.validateFax();
            this.validateIEInner();
            this.validateLatitudeInner();
            this.validateLongitudeInner();
            this.validateEmail1();
            this.validateEmail2();
            this.validateURLInner();
            this.validatePorcentagemInner();
        },
        validateCNPJInner() {
            if (
                this.required.cnpj.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.cnpj)
            ) {
                this.invalidCNPJ = true;
                this.invalidCNPJMsg = 'msg-campo-obrigatorio';
            } else if (!this.isNull(this.empresa.cnpj) && !this.validateCnpj(this.empresa.cnpj)) {
                this.invalidCNPJ = true;
                this.invalidCNPJMsg = 'msg-campo-invalido';
            } else {
                this.invalidCNPJ = false;
            }
        },
        validateCEPInner() {
            if (
                this.required.cep.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.cep)
            ) {
                this.invalidCEP = true;
                this.invalidCEPMsg = 'msg-campo-obrigatorio';
            } else if (!this.isNull(this.empresa.cep) && !this.validateCEP(this.empresa.cep)) {
                this.invalidCEP = true;
                this.invalidCEPMsg = 'msg-campo-invalido';
            } else {
                this.invalidCEP = false;
            }
        },
        validateTelefone() {
            if (
                this.required.telefone.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.telefone)
            ) {
                this.invalidTelefone = true;
                this.invalidTelefoneMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.telefone) &&
                !this.validatePhone(this.empresa.telefone)
            ) {
                this.invalidTelefone = true;
                this.invalidTelefoneMsg = 'msg-campo-invalido';
            } else {
                this.invalidTelefone = false;
            }
        },
        validateCelular() {
            if (
                this.required.telefone_celular.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.telefone_celular)
            ) {
                this.invalidCelular = true;
                this.invalidCelularMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.telefone_celular) &&
                !this.validatePhone(this.empresa.telefone_celular)
            ) {
                this.invalidCelular = true;
                this.invalidCelularMsg = 'msg-campo-invalido';
            } else {
                this.invalidCelular = false;
            }
        },
        validateFax() {
            if (!this.isNull(this.empresa.fax) && !this.validatePhone(this.empresa.fax)) {
                this.invalidFax = true;
                this.invalidFaxMsg = 'msg-campo-invalido';
            } else {
                this.invalidFax = false;
            }
        },
        validateIEInner() {
            if (this.required.ie.includes(this.$route.meta.abrev) && this.isNull(this.empresa.ie)) {
                this.invalidIE = true;
                this.invalidIEMsg = 'msg-campo-obrigatorio';
            } else if (!this.isNull(this.empresa.ie) && this.isNull(this.empresa.uf) && this.empresa.ie != "ISENTO") {
                this.msgs.push(new Message('alert', 'msg-problema-preencher-cidade-empresa', true));
                this.invalidIE = true;
                this.invalidIEMsg = 'msg-campo-invalido';
            } else if (
                !this.isNull(this.empresa.ie) &&
                !InscricaoEstadual.validateIE(this.empresa.ie, this.empresa.uf) &&
                this.empresa.ie != "ISENTO"
            ) {
                this.invalidIE = true;
                this.invalidIEMsg = 'msg-campo-invalido';
            } else {
                this.invalidIE = false;
            }
        },
        validatePorcentagemInner() {
            if (
                this.required.porcentagemestipulada.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.porcentagemestipulada)
            ) {
                this.invalidPorcentagem = true;
                this.invalidPorcentagemMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.porcentagemestipulada) &&
                !this.validatePorcentagem(this.empresa.porcentagemestipulada)
            ) {
                this.invalidPorcentagem = true;
                this.invalidPorcentagemMsg = 'msg-campo-invalido-porcentagem';
            } else {
                this.invalidPorcentagem = false;
            }
        },
        validateLatitudeInner() {
            if (
                this.required.latitude.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.latitude)
            ) {
                this.invalidLatitude = true;
                this.invalidLatitudeMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.latitude) &&
                !this.validateLatitude(this.empresa.latitude)
            ) {
                this.invalidLatitude = true;
                this.invalidLatitudeMsg = 'msg-campo-invalido';
            } else {
                this.invalidLatitude = false;
            }
        },
        validateLongitudeInner() {
            if (
                this.required.longitude.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.longitude)
            ) {
                this.invalidLongitude = true;
                this.invalidLongitudeMsg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.longitude) &&
                !this.validateLongitude(this.empresa.longitude)
            ) {
                this.invalidLongitude = true;
                this.invalidLongitudeMsg = 'msg-campo-invalido';
            } else {
                this.invalidLongitude = false;
            }
        },
        validateEmail1() {
            if (
                this.required.email1.includes(this.$route.meta.abrev) &&
                this.isNull(this.empresa.email1)
            ) {
                this.invalidEmail1 = true;
                this.invalidEmail1Msg = 'msg-campo-obrigatorio';
            } else if (
                !this.isNull(this.empresa.email1) &&
                !this.validateEmail(this.empresa.email1)
            ) {
                this.invalidEmail1 = true;
                this.invalidEmail1Msg = 'msg-campo-invalido';
            } else {
                this.invalidEmail1 = false;
            }
        },
        validateEmail2() {
            if (!this.isNull(this.empresa.email2) && !this.validateEmail(this.empresa.email2)) {
                this.invalidEmail2 = true;
                this.invalidEmail2Msg = 'msg-campo-invalido';
            } else {
                this.invalidEmail2 = false;
            }
        },
        validateURLInner() {
            if (!this.isNull(this.empresa.paginaweb) && !this.validateURL(this.empresa.paginaweb)) {
                this.invalidURL = true;
                this.invalidURLMsg = 'msg-campo-invalido';
            } else {
                this.invalidURL = false;
            }
        },
        getInfoByCNPJ() {
            new Promise((resolve, reject) => {
                if (this.cidade == null) {
                    this.$refs.cidadeInput.focusedOut = true;
                    reject('msg-erro-pesquisa-cnpj-cidade');
                } else if (this.invalidCNPJ) {
                    reject('msg-erro-pesquisa-cnpj-invalido');
                }
                resolve();
            })
                .then((_) => {
                    this.isSearchingCNPJ = true;
                    this.service
                        .getInfoByCNPJ(this.cidade.estado, this.empresa.cnpj)
                        .then((res) => {
                            if (res.status != 200) {
                                if (res.status == 404) {
                                    console.error(`CNPJ ${cnpj} não existe.`);
                                    this.msgs.push(
                                        new Message('error', 'msg-erro-cnpj-inexistente', true)
                                    );
                                } else {
                                    console.error(`Retorno: ${res.status}`);
                                    this.msgs.push(
                                        new Message('error', 'msg-erro-pesquisa-cnpj', true)
                                    );
                                }
                            } else {
                                let r = JSON.parse(res.content);
                                let data = null;
                                if (typeof r == 'object') {
                                    r.forEach((e) => {
                                        if (e.situacao == '1') data = e;
                                    });
                                } else if (r.length > 0) {
                                    data = r[r.length - 1];
                                }
                                if (data != null) {
                                    this.empresa.nome = data.nome;
                                    this.empresa.cep = data.cep;
                                    this.empresa.endereco = data.logradouro;
                                    this.empresa.numero = data.numero;
                                    this.empresa.bairro = data.bairro;
                                    if(this.contribuinteSelected.id === "C") this.empresa.ie = data.ie;
                                }
                            }
                        })
                        .catch((e) => {
                            console.error(e);
                            this.msgs.push(new Message('error', 'msg-erro-pesquisa-cnpj', true));
                        })
                        .finally((_) => {
                            this.isSearchingCNPJ = false;
                        });
                })
                .catch((e) => {
                    this.msgs.push(new Message('error', e, true));
                });
        },
        getInfoByCEP() {
            this.validateCEPInner();
            if (this.invalidCEP) {
                this.msgs.push(new Message('error', 'msg-erro-pesquisa-cep-invalido', true));
            } else {
                let cep = this.empresa.cep;
                this.isSearchingCEP = true;
                let token = this.$moment().format('!%$MM(yyyy)DD*&');
                token = this.$CryptoJS.MD5(token).toString();

                this.service
                    .getInfoByCEP(cep, token)
                    .then((data) => {
                        this.buscaCep = data.content;
                        this.empresa.bairro = data.content.bairro;
                        this.empresa.endereco = data.content.logradouro;
                        this.loadCidade(this.removeAcentos(data.content.cidade));
                    })
                    .catch((err) => {
                        console.error(err);
                        this.msgs.push(new Message('error', 'msg-erro-pesquisa-cep', true));
                    })
                    .finally((_) => {
                        this.isSearchingCEP = false;
                    });
            }
        },
        loadCidade(cidade, uf = null) {
            this.cidadeservice
                .findAllCombo(this.isNull(cidade) ? '.*' : this.removeToSearch(cidade))
                .then((body) => {
                    return body.content.map((i) => {
                        return {
                            id: i.idcidade,
                            cidade: i.cidade,
                            nome: i.descricao,
                            estado: i.estado.descricao,
                        };
                    });
                })
                .then((allcidades) => {
                    this.cidades = Object.assign(allcidades);
                    this.cidades.sort();
                }, 350)
                .finally((_) => {
                    // foi colcoado aqui para a busca com cep funcionar até com conexão fraca a internet
                    if (!this.isNull(this.buscaCep)) {
                        let idcidade = this.cidades.filter((s) => s.estado == this.buscaCep.uf);
                        if (idcidade.length > 0) {
                            this.loadCidadeById(idcidade[0].id);
                        } else {
                            this.msgs.push(new Message('alert', 'cidade-nao-encontrada', true));
                        }
                        this.buscaCep = null;
                    } else if (uf != null) {
                        let idcidade = this.cidades.filter((s) => s.estado == uf);
                        if (idcidade.length > 0) {
                            this.loadCidadeById(idcidade[0].id);
                        }
                    }
                });
        },
        loadMoedas() {
            this.isLoading = true;
            this.moedaservice
                .findAllCombo('')
                .then((body) => {
                    return body.content.map((r) => {
                        return {
                            id: r.idmoeda,
                            label: `${r.simbolomonetario} - ${r.descricao}`,
                        };
                    });
                })
                .then((r) => {
                    this.moedas = r;
                    this.moeda = this.moedas.find((element) => element.id === 1);
                })
                .catch((e) => {
                    console.error('Erro ao carregar moedas: ' + e);
                })
                .finally(() => {
                    if (this.isUpdate) {
                        let tempMoeda = this.moedas.filter((e) => e.id == this.empresa.idmoeda);
                        this.moeda = tempMoeda.length > 0 ? tempMoeda[0] : this.moedas[0];
                    } else {
                        this.moeda = this.moedas[0];
                    }
                    this.isLoading = false;
                });
        },
        loadCidadeById(id) {
            this.cidadeservice
                .find(id)
                .then((cidade) => {
                    if (cidade.content != null) {
                        let temp = {
                            id: cidade.content.id,
                            nome: cidade.content.descricao,
                        };
                        this.cidade = temp;
                    } else {
                        this.msgs.push(new Message('error', 'msg-erro-load', true));
                    }
                }, 350)
                .catch((e) => {
                    console.error(e);
                    this.msgs.push(new Message('error', 'msg-erro-load', true));
                });
        },
        updateCidade() {
            if (this.cidade != null) {
                let cidadeTemp = this.cidades.filter((s) => s.id == this.cidade.id)[0];
                if (cidadeTemp != undefined) {
                    this.empresa.idcidade = cidadeTemp.id;
                    this.empresa.cidade = cidadeTemp.cidade;
                    this.empresa.uf = cidadeTemp.estado;
                }
            }
        },
        copyWhatsApp() {
            this.copyWhatsAppActive = true;
            this.empresa.fax = this.empresa.telefone_celular;
            setTimeout(() => {
                this.copyWhatsAppActive = false;
            }, 100);
        },
        copyEmail() {
            this.empresa.email2 = this.empresa.email1;
            this.validateEmail2();
        },

        openLink(link) {
            if (!this.isNull(link)) {
                if (!link.startsWith('http') || !link.startsWith('https')) link = `https://${link}`;
                window.open(link.toLowerCase(), '_blank');
            }
        },

        openLocationEditor() {
            if (this.isUpdate) {
                if (
                    !this.onlyNumbers(this.empresa.latitude) ||
                    !this.onlyNumbers(this.empresa.longitude)
                ) {
                    this.center.lat = 0;
                    this.center.lng = 0;
                } else {
                    this.center.lat = +this.empresa.latitude;
                    this.center.lng = +this.empresa.longitude;
                }
            }
            if (
                !this.isUpdate &&
                this.isNull(this.empresa.latitude) &&
                this.isNull(this.empresa.longitude)
            ) {
                if (!this.isNull(this.empresa.endereco))
                    this.startSearchWith = this.empresa.endereco;
                if (!this.isNull(this.empresa.numero))
                    this.startSearchWith = this.startSearchWith + ' ' + this.empresa.numero;
                if (!this.isNull(this.empresa.bairro))
                    this.startSearchWith = this.startSearchWith + ' ' + this.empresa.bairro;
                if (!this.isNull(this.empresa.cidade))
                    this.startSearchWith = this.startSearchWith + ' ' + this.empresa.cidade;
                if (!this.isNull(this.empresa.uf))
                    this.startSearchWith = this.startSearchWith + ' ' + this.empresa.uf;
                setTimeout(() => {
                    this.$children[this.$children.length - 1].getLocation(this.startSearchWith);
                }, 1000);
            }
            this.locationEditorOpen = true;
        },
        onCancelLocationEditor() {
            this.locationEditorOpen = false;
            this.startSearchWith = '';
        },
        updateLocation(val) {
            this.center.lat = val.center.lat;
            this.center.lng = val.center.lng;
            this.empresa.latitude = this.center.lat;
            this.empresa.longitude = this.center.lng;
            this.onCancelLocationEditor();
            this.validateLatitudeInner();
            this.validateLongitudeInner();
        },
        redirectToPage() {
            let tempTela = null;

            if (this.$route.query.tela == 'cliente-empresa') {
                tempTela = `${this.$route.meta.abrev}-cliente-dados-cadastrais-edit`;
            } else if (this.$route.query.tela == 'produto-empresa') {
                tempTela = `${this.$route.meta.abrev}-produto-dados-cadastrais-edit`;
            }
            setTimeout(() => {
                this.$router.push({
                    name: tempTela,
                    params: { id: this.$route.query['id'] },
                    query: {
                        dadoscadastrais: this.$route.query.dadoscadastrais,
                        politicacomercial: this.$route.query.politicacomercial,
                        analisecomercial: this.$route.query.analisecomercial,
                    },
                });
            }, 1000);
        },
        getLogo(logo) {
            if (logo && logo.length > 0) {
                this.logo = logo;
                this.empresa.logo_url_print = logo[0].imgurl;
            }
            this.$forceUpdate();
        },
        showMessage(type, msg) {
            this.msgs.push(new Message(type, msg, true));
        },
    },
};
</script>
<style lang="scss" scoped>
.page-container {
    .loading {
        opacity: 0.5;
        pointer-events: none;
    }
    .img-picker {
        height: 235px;
    }
}
.botao-localizacao {
    margin-top: 22px;
    width: 100%;
    height: 40px;
    font-size: 13px;
    padding: 0;
}

@media (max-width: 1460px) {
    .botao-localizacao {
        font-size: 12px;
    }
}

@media (max-width: 1297px) {
    .botao-localizacao {
        font-size: 11px;
    }
}

@media (max-width: 1234px) {
    .botao-localizacao {
        font-size: 10px;
    }
}
</style>